import {createSelector} from 'reselect';

import {TYP_UPSELL_TYPES} from 'constants/product';
import type {
    OrderLastType,
    OrderType,
    SuccessPageReducerType,
} from 'desktop/pages/success/types/types';
import {UpsellType, UpsellsStateType} from 'desktop/pages/success/types/upsellsTypes';
import {ApplicationStoreType} from 'desktop/store';

import {MODULE_KEY_SUCCESS} from '../constants';

/**
 * The Success page's root reducer's state
 *
 * @param {ApplicationStoreType} store - Application store
 * @returns {SuccessPageReducerType} - state of the root reducer
 */
export const getSuccessPageState = (store: ApplicationStoreType): SuccessPageReducerType =>
    store[MODULE_KEY_SUCCESS];

/**
 * The Success page's upsells reducer's state
 *
 * @param {ApplicationStoreType} store - Application store
 * @returns {UpsellsStateType} - upsells reducer state
 */
export const getUpsellsState = (store: ApplicationStoreType): UpsellsStateType =>
    getSuccessPageState(store).upsells;

/**
 * The Success page's order reducer's state
 *
 * @param {ApplicationStoreType} store - Application store
 * @returns {UpsellsStateType} - order reducer state
 */
export const getOrderState = (store: ApplicationStoreType): OrderType =>
    getSuccessPageState(store).order;

/**
 * Get order id
 * @param {ApplicationStoreType} store - Application store
 * @returns {number} Order id
 */
export const getOrderId = (store: ApplicationStoreType): string =>
    /* TODO - it used in
        /modules/features/contacts/components/confirmButton/confirmButton.tsx
        /modules/features/contacts/components/successMessage/successMessage.tsx
        /modules/sagas/dynamicYield/thankPage/thankPage.js
    */
    getOrderState(store).incrementId;

/**
 * The Success page's upsellTimer reducer's state
 *
 * @param {ApplicationStoreType} store - Application store
 * @returns {UpsellsStateType} - upsellTimer reducer state
 */
export const getUpsellTimerState = (store: ApplicationStoreType): number =>
    getSuccessPageState(store).upsellTimer;

/**
 * Get all upsells (both upgrades and accessories) in success page
 * @param {ApplicationStoreType} store - Application store
 * @returns {Array<UpsellType>} - Upsells in success page
 */
export const getUpsells = (store: ApplicationStoreType): Array<UpsellType> =>
    getUpsellsState(store).list;

/**
 * Get upgrades in success page
 * @param {ApplicationStoreType} store - Application store
 * @returns {Array<UpsellType>} - Upgrades in success page
 */
export const getUpgrades = createSelector(getUpsells, (upsells: Array<UpsellType>) =>
    upsells.filter(({type}: UpsellType) => type !== TYP_UPSELL_TYPES.ACCESSORY),
);

/**
 * Get accessories in success page
 * @param {ApplicationStoreType} store - Application store
 * @returns {Array<UpsellAccessoryType>} - Accessories in success page
 */
export const getAccessories = createSelector(
    getUpsells,
    (upsells: Array<UpsellType>): Array<UpsellType> =>
        upsells.filter(({type}: UpsellType) => type === TYP_UPSELL_TYPES.ACCESSORY),
);

/**
 * Return accessories grouped by window accessories
 * @param {ApplicationStoreType} store - Application store
 * @returns {{[string]: Array<UpsellAccessoryType>}} - simple accessories grouped by window accessories
 */
export const getAccessoriesGroupedByParent = createSelector(
    getAccessories,
    (accessories: Array<UpsellType>) =>
        accessories
            .map(({color_image: colorImage, ...upsell}: UpsellType) => ({
                ...upsell,
                color: {
                    color_name: upsell.color_name,
                    id: upsell.id,
                    image: colorImage,
                },
            }))
            .reduce(
                (accumulator: Record<string, Array<UpsellType>>, upsell: UpsellType) => ({
                    ...accumulator,
                    // @ts-expect-error
                    [upsell.parentId]: accumulator[upsell.parentId]
                        ? // @ts-expect-error
                          [...accumulator[upsell.parentId], upsell]
                        : [upsell],
                }),
                {},
            ),
);

/**
 * Get added upgrades in success page
 * @param {ApplicationStoreType} store - Application store
 * @returns {Array<UpsellType>} - Added upgrades in success page
 */
export const getAddedUpgrades = createSelector(getUpgrades, (upsells: Array<UpsellType>) =>
    upsells.filter(({isAdded}: UpsellType) => isAdded),
);

/**
 * Get added accessories in success page
 * @param {ApplicationStoreType} store - Application store
 * @returns {Array<UpsellAccessoryType>} - Added accessories in success page
 */
export const getAddedAccessories = createSelector(getAccessories, (upsells: Array<UpsellType>) =>
    upsells.filter(({isAdded}: UpsellType) => isAdded),
);

/**
 * Has upsells in success page
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} - has upgrades
 */
export const hasUpgrades = createSelector(
    getUpgrades,
    (upgrades: Array<UpsellType>) => upgrades.length > 0,
);

/**
 * Has accessories in success page
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} - has upgrades
 */
export const hasAccessories = createSelector(
    getAccessories,
    (accessories: Array<UpsellType>) => accessories.length > 0,
);

/**
 * Get selected upsell from thankyou page
 * @param {ApplicationStoreType} store - Application store
 * @returns {UpsellType} Active Upsell
 */
export const getActiveUpsell = (store: ApplicationStoreType): UpsellType =>
    store[MODULE_KEY_SUCCESS].activeUpsell;

/**
 * Get payment details for success page
 * @param {ApplicationStoreType} store - app store
 * @returns {OrderLastType} payment details
 */
export const getOrderLastData = (store: ApplicationStoreType): OrderLastType =>
    getSuccessPageState(store).orderLast;

/**
 * Get order id
 * @param {ApplicationStoreType} store - Application store
 * @returns {number} Order id
 */
export const getPaymentDetailsId = (store: ApplicationStoreType): string =>
    /* TODO - it used in
        /modules/features/contacts/components/confirmButton/confirmButton.tsx
        /modules/features/contacts/components/successMessage/successMessage.tsx
        /modules/sagas/dynamicYield/thankPage/thankPage.js
    */
    getOrderLastData(store).id;

/**
 * Return name of active upsell on success page
 * @param {ApplicationStoreType} store - Application store
 * @return {string} upsell name
 */
export const getActiveUpsellName = (store: ApplicationStoreType): string => {
    const {title, type} = getActiveUpsell(store);
    return `${title === 'Digital Block coating' ? 'Blue Light coating' : title}${
        type === 'package' ? ' package' : ''
    }`;
};
